import {
    Fragment,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    evaluateOutreachStepDelayInput,
    getOutreachStepDelayInputValue,
    normalizeOutreachStepDelayInMinutes,
    OUTREACH_STEP_DELAY_INPUT_PATTERN,
    type OutreachStepDelayPartsInput,
    outreachStepDelaySupportsUnitToggle,
} from '@apps/web/lib/outreach_step_delay_utils';
import { type OutreachType } from '@apps/web/lib/outreach_step_types';
import { cn } from '@apps/web/lib/utils';

type OutreachStepDelayInputProps = {
    readonly index: number;
    readonly outreachType: OutreachType;
    readonly delayInMinutes: number;
    readonly inputLabel: string;
    readonly label?: ReactNode;
    readonly className?: string;
    readonly onDelayChange: (delayInMinutes: number) => void;
};

export function OutreachStepDelayInput({
    index,
    outreachType,
    delayInMinutes,
    inputLabel,
    label,
    className,
    onDelayChange,
}: OutreachStepDelayInputProps) {
    const [rawValue, setRawValue] = useState<OutreachStepDelayPartsInput>(() =>
        getOutreachStepDelayInputValue({
            index,
            outreachType,
            delayInMinutes,
        }),
    );
    const [isFocused, setIsFocused] = useState(false);
    const supportsHoursMinutes = outreachStepDelaySupportsUnitToggle({
        index,
        outreachType,
    });

    const evaluation = useMemo(
        () =>
            evaluateOutreachStepDelayInput({
                index,
                outreachType,
                input: rawValue,
            }),
        [index, outreachType, rawValue],
    );

    const normalizedCurrentDelay = useMemo(
        () =>
            normalizeOutreachStepDelayInMinutes({
                index,
                outreachType,
                delayInMinutes,
            }),
        [delayInMinutes, index, outreachType],
    );
    const invalidParts = evaluation.invalidParts;

    const resetToDelay = useCallback(() => {
        setRawValue(
            getOutreachStepDelayInputValue({
                index,
                outreachType,
                delayInMinutes: normalizedCurrentDelay,
            }),
        );
    }, [index, normalizedCurrentDelay, outreachType]);

    const getDelayFromDraftOrCurrent = useCallback(
        (input: OutreachStepDelayPartsInput) => {
            const nextEvaluation = evaluateOutreachStepDelayInput({
                index,
                outreachType,
                input,
            });
            if (nextEvaluation.status === 'valid') {
                return nextEvaluation.delayInMinutes;
            }
            return normalizedCurrentDelay;
        },
        [index, normalizedCurrentDelay, outreachType],
    );

    useEffect(() => {
        if (!isFocused) {
            resetToDelay();
        }
    }, [isFocused, resetToDelay]);

    const handlePartChange = useCallback(
        (part: keyof OutreachStepDelayPartsInput, nextPartValue: string) => {
            const nextValue = {
                ...rawValue,
                [part]: nextPartValue,
            };
            const nextEvaluation = evaluateOutreachStepDelayInput({
                index,
                outreachType,
                input: nextValue,
            });
            if (nextEvaluation.status !== 'valid') {
                setRawValue(nextValue);
                return;
            }

            setRawValue(
                nextEvaluation.shouldReplaceInput
                    ? nextEvaluation.displayValue
                    : nextValue,
            );
            onDelayChange(nextEvaluation.delayInMinutes);
        },
        [index, onDelayChange, outreachType, rawValue],
    );

    const handleBlur = useCallback(() => {
        const nextDelayInMinutes = getDelayFromDraftOrCurrent(rawValue);
        setRawValue(
            getOutreachStepDelayInputValue({
                index,
                outreachType,
                delayInMinutes: nextDelayInMinutes,
            }),
        );
        onDelayChange(nextDelayInMinutes);
        setIsFocused(false);
    }, [
        getDelayFromDraftOrCurrent,
        index,
        onDelayChange,
        outreachType,
        rawValue,
    ]);

    const commonInputProps = {
        type: 'text',
        inputMode: 'numeric' as const,
        pattern: OUTREACH_STEP_DELAY_INPUT_PATTERN,
        onFocus: () => setIsFocused(true),
        onBlur: handleBlur,
    };

    return (
        <div className={cn('flex flex-wrap items-center gap-2', className)}>
            {label ? (
                <Label className="text-muted-foreground text-sm">{label}</Label>
            ) : null}
            {supportsHoursMinutes ? (
                <Fragment>
                    <Input
                        {...commonInputProps}
                        aria-label={`${inputLabel} hours`}
                        aria-invalid={invalidParts.hours}
                        value={rawValue.hours}
                        onChange={(event) =>
                            handlePartChange('hours', event.target.value)
                        }
                        className="w-16"
                    />
                    <span className="text-muted-foreground text-sm">hours</span>
                    <Input
                        {...commonInputProps}
                        aria-label={`${inputLabel} minutes`}
                        aria-invalid={invalidParts.minutes}
                        value={rawValue.minutes}
                        onChange={(event) =>
                            handlePartChange('minutes', event.target.value)
                        }
                        className="w-16"
                    />
                    <span className="text-muted-foreground text-sm">
                        minutes
                    </span>
                </Fragment>
            ) : (
                <Fragment>
                    <Input
                        {...commonInputProps}
                        aria-label={`${inputLabel} minutes`}
                        aria-invalid={invalidParts.minutes}
                        value={rawValue.minutes}
                        onChange={(event) =>
                            handlePartChange('minutes', event.target.value)
                        }
                        className="w-20"
                    />
                    <span className="text-muted-foreground text-sm">
                        minutes
                    </span>
                </Fragment>
            )}
        </div>
    );
}
